import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Divider,
  TablePagination
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Page from '../../../components/Page';
import Loader from '../../../components/Loader';
import { FaEye, FaPhoneAlt, FaEnvelope, FaWhatsapp } from 'react-icons/fa';
import { fetchAdsPerformance } from '../../../services/company-stats';
import { AdsPerformanceResponse } from '../../../services/company-stats/types';

const CompanyAdsPerformanceDetails = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AdsPerformanceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchAdsPerformanceData = async () => {
      if (!userId) {
        setError('User ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await fetchAdsPerformance(userId, {
          limit: rowsPerPage,
          skip: page * rowsPerPage,
        });
        setData(result);
        setError(null);
      } catch (err) {
        console.error('Error fetching ads performance:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchAdsPerformanceData();
  }, [userId, page, rowsPerPage]);

  const handleGoBack = () => {
    navigate('/company-ads-performance');
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'published':
        return 'success';
      case 'pending':
      case 'submitted':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'suspended':
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Page title="KPI Dashboard Details">
        <Container>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Loader />
          </Box>
        </Container>
      </Page>
    );
  }

  if (error || !data) {
    return (
      <Page title="KPI Dashboard Details">
        <Container>
          <Box sx={{ pt: 3, pb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4">Error</Typography>
            <Button
              onClick={handleGoBack}
              variant='outlined'
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </Box>
          <Typography color="error">{error || 'No data available'}</Typography>
        </Container>
      </Page>
    );
  }

  return (
    <Page title={`KPI Dashboard: ${data.name}`}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ pt: 3, pb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4">{data.name}</Typography>
            <Typography variant="body1" color="text.secondary">
              {data.email}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Chip 
                label={data.userRole === 'agent' ? 'Agent' : 'Company Admin'} 
                size="small" 
                color={data.userRole === 'agent' ? 'success' : 'primary'}
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>
          <Button
            onClick={handleGoBack}
            variant='outlined'
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', bgcolor: 'white' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Box sx={{ mb: 2, p: 1.5, borderRadius: '50%', bgcolor: '#1976d220', color: '#1976d2', display: 'flex' }}>
                  <FaEye size={20} />
                </Box>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'medium' }}>
                  {(data.totalViews || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                  Total Ad Views
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', bgcolor: 'white' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Box sx={{ mb: 2, p: 1.5, borderRadius: '50%', bgcolor: '#2e7d3220', color: '#2e7d32', display: 'flex' }}>
                  <FaPhoneAlt size={20} />
                </Box>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'medium' }}>
                  {(data.totalContactClicks || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                  Total Contact Clicks
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', bgcolor: 'white' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
                  <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ bgcolor: '#e5737320', color: '#e57373', p: 1.5, borderRadius: '50%', display: 'flex', mb: 2 }}>
                      <FaPhoneAlt size={20} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
                      {data.ads.reduce((sum, ad) => sum + (ad.clickBreakdown?.call || 0), 0).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ bgcolor: '#4caf5020', color: '#4caf50', p: 1.5, borderRadius: '50%', display: 'flex', mb: 2 }}>
                      <FaWhatsapp size={20} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
                      {data.ads.reduce((sum, ad) => sum + (ad.clickBreakdown?.whatsapp || 0), 0).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ bgcolor: '#42a5f520', color: '#42a5f5', p: 1.5, borderRadius: '50%', display: 'flex', mb: 2 }}>
                      <FaEnvelope size={20} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
                      {data.ads.reduce((sum, ad) => sum + (ad.clickBreakdown?.email || 0), 0).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                  Contact Click Breakdown
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Ads Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Advertisement Performance
            </Typography>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">S.No</TableCell>
                    <TableCell align="left">Ad Title</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Ad Views</TableCell>
                    <TableCell align="center">Contact Clicks</TableCell>
                    <TableCell align="center">Contact Click Breakdown</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.ads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No ads found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.ads.map((ad, index) => (
                      <TableRow key={ad.adId} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                        <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell align="left" component="th" scope="row">
                          {ad.title}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={ad.status}
                            size="small"
                            color={getStatusColor(ad.status) as any}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">{(ad.totalViews || 0).toLocaleString()}</TableCell>
                        <TableCell align="center">{(ad.totalContactClicks || 0).toLocaleString()}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">Call</Typography>
                              <Typography variant="body2">{ad.clickBreakdown?.call || 0}</Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem />
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">WhatsApp</Typography>
                              <Typography variant="body2">{ad.clickBreakdown?.whatsapp || 0}</Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem />
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">Email</Typography>
                              <Typography variant="body2">{ad.clickBreakdown?.email || 0}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={data.total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default CompanyAdsPerformanceDetails;

